import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { HeaderProduto } from "components/HeaderProduto";
import { StarDecorations } from "components/ui/StarDecorations";
import { CardIniciativaELBB } from "components/ui/CardIniciativaELBB";
import { CardIniciativaConexao } from "components/ui/CardIniciativaConexao";
import { buscarEntregasPreInscricao, invalidarEntregasPreInscricao } from "services/pre-inscricao.service";
import { useAuth } from "context/AuthContext";
import { FiCalendar } from "react-icons/fi";
import { PHASE_IDS } from "utils/constants";
import { useActivePhases, usePeriodoFase } from "hooks/useControlDate";
import { formatarPeriodo } from "utils/cronograma";
import { getProduto } from "config/produto-env";
import { ROTA_PRE_INSCRICAO } from "utils/destino-inicial";
import { normalizeCategoryPremio } from "utils/iniciativa-categoria";
import { CategoriaPreInscricao, getCategoriasDoProduto, getCategoriasPermitidas } from "utils/pessoa";
import { getVagasPreInscricao } from "utils/vagas-pre-inscricao";

export function Listagem() {
  const [entregas, setEntregas] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [erroBusca, setErroBusca] = useState(false);
  const [expandedCards, setExpandedCards] = useState<Set<number>>(new Set());
  const { user } = useAuth();
  const navigate = useNavigate();
  const phases = useActivePhases();

  const produto = getProduto();

  // Separa iniciativas próprias das que o usuário participa como membro da equipe
  const minhasIniciativas = entregas.filter((e) => e.owner === user?._id);
  const participando = entregas.filter(
    (e) => e.owner !== user?._id && e.team?.some((m: any) => m._id === user?._id)
  );

  const headingRef = useRef<HTMLHeadingElement>(null);

  const isElbb = produto === "PREMIO_ELBB";
  const preInscricaoAberta = isElbb ? phases.elbbPreRegistration : phases.conexaoPreRegistration;
  const periodoCadastro = formatarPeriodo(
    usePeriodoFase(isElbb ? PHASE_IDS.ELBB_PRE_REGISTRATION : PHASE_IDS.CONEXAO_PRE_REGISTRATION)
  );
  // A apreciação pelo gestor só existe no Prêmio BB.
  const periodoApreciacao = formatarPeriodo(usePeriodoFase(PHASE_IDS.CONEXAO_MANAGER_APPROVAL));
  const apreciacaoAberta = !isElbb && phases.conexaoManagerApproval;

  const cadastroAberto = isElbb
    ? phases.canAccessCadastroELBB
    : phases.canAccessCadastroConexao && !phases.conexaoManagerApproval;
  // Só oferece o cadastro a quem a base permite (programa, inelegibilidade e equipe organizadora) e ainda tem vaga.
  const podeCadastrar =
    getVagasPreInscricao(getCategoriasPermitidas(user?.pessoa, produto), entregas, user?._id).categorias.length > 0;
  const rotaCadastro = cadastroAberto && podeCadastrar ? ROTA_PRE_INSCRICAO : null;

  const CardComponent = isElbb ? CardIniciativaELBB : CardIniciativaConexao;
    
  const carregarEntregas = async () => {
    try {
      setLoading(true);
      setErroBusca(false);
      invalidarEntregasPreInscricao();
      const data = await buscarEntregasPreInscricao();
      // Só o produto do site: o aggregate traz as iniciativas da pessoa em qualquer prêmio.
      const categoriasDoProduto = getCategoriasDoProduto(produto);
      setEntregas(
        data.filter((e: any) =>
          categoriasDoProduto.includes(normalizeCategoryPremio(e.categoryPremio) as CategoriaPreInscricao)
        )
      );
    } catch (error) {
      setEntregas([]);
      setErroBusca(true);
    } finally {
      setLoading(false);
    }
  };
  
  useEffect(() => {
    if (user?._id) {
      carregarEntregas();
    }
  }, [user]);

  useEffect(() => {
    headingRef.current?.focus();
  }, []);

  const toggleCard = (index: number) => {
    setExpandedCards((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(index)) {
        newSet.delete(index);
      } else {
        newSet.add(index);
      }
      return newSet;
    });
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-bb via-blue-bb-1 to-blue-bb-5 overflow-x-hidden relative">
      <HeaderProduto currentPage="pre-inscricao" />

      {/* Star Decorations - Hidden on Mobile */}
      <div aria-hidden="true" className="hidden md:block pointer-events-none">
        <StarDecorations count={18} density="medium" />
      </div>

      <div className="container mx-auto px-4 sm:px-6 py-6 sm:py-8 lg:py-12 max-w-6xl">
        <header className="mb-6 sm:mb-7 lg:mb-8" tabIndex={0}>
          <h1
            ref={headingRef}
            tabIndex={-1}
            className="text-white text-2xl sm:text-3xl lg:text-4xl font-bold mb-3 sm:mb-4 leading-tight outline-none"
          >
            Acompanhe a validação da sua iniciativa
          </h1>
          {preInscricaoAberta && periodoCadastro && (
            <div className="flex flex-col sm:flex-row items-start sm:items-center gap-1.5 sm:gap-2 text-yellow-400 text-sm sm:text-base">
              <FiCalendar className="w-4 h-4 sm:w-5 sm:h-5 flex-shrink-0" aria-hidden="true" />
              <span className="font-semibold">PERÍODO PARA CADASTRO: DE {periodoCadastro}</span>
            </div>
          )}
          {apreciacaoAberta && periodoApreciacao && (
            <div className="flex flex-col sm:flex-row items-start sm:items-center gap-1.5 sm:gap-2 text-white text-sm sm:text-base mt-1.5">
              <FiCalendar className="w-4 h-4 sm:w-5 sm:h-5 flex-shrink-0" aria-hidden="true" />
              <span className="font-semibold">ETAPA ATUAL - APRECIAÇÃO: DE {periodoApreciacao}</span>
            </div>
          )}
        </header>

        <main className="bg-white rounded-xl sm:rounded-2xl p-4 sm:p-6 lg:p-8 shadow-xl sm:shadow-2xl">
          {loading ? (
            <p role="status" aria-live="polite" className="text-gray-600 text-center text-base sm:text-lg py-8">
              Carregando iniciativas...
            </p>
          ) : erroBusca ? (
            <p role="alert" className="text-red-600 text-center text-base sm:text-lg py-8">
              Não foi possível carregar as iniciativas. Tente novamente em instantes.
            </p>
          ) : (
            <>
              {/* Seção: Iniciativas inscritas (owner) */}
              <section aria-labelledby="secao-inscritas" className="mb-8 sm:mb-10">
                <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 sm:gap-4 mb-5 sm:mb-6">
                  <h2 id="secao-inscritas" className="text-lg sm:text-xl font-semibold text-gray-800" tabIndex={0}>
                    Iniciativas inscritas:
                  </h2>
                  {rotaCadastro && (
                    <button
                      onClick={() => navigate(rotaCadastro)}
                      aria-label="Adicionar nova iniciativa"
                      className="w-full sm:w-auto text-blue-600 font-semibold hover:text-blue-700 active:text-blue-800 transition-colors text-sm sm:text-base py-2 sm:py-0 px-4 sm:px-0 bg-blue-50 sm:bg-transparent rounded-lg sm:rounded-none touch-manipulation"
                    >
                      + ADICIONAR INICIATIVA
                    </button>
                  )}
                </div>

                {minhasIniciativas.length === 0 ? (
                  <p className="text-gray-500 text-sm sm:text-base">
                    Você ainda não cadastrou nenhuma iniciativa.
                  </p>
                ) : (
                  <div className="space-y-3 sm:space-y-4">
                    {minhasIniciativas.map((entrega, index) => (
                      <CardComponent
                        key={entrega._id || index}
                        entrega={entrega}
                        index={index}
                        isExpanded={expandedCards.has(index)}
                        onToggle={() => toggleCard(index)}
                        onUpdate={carregarEntregas}
                        currentUserId={user?._id}
                      />
                    ))}
                  </div>
                )}
              </section>

              {/* Seção: Iniciativas em que participo como membro da equipe */}
              {participando.length > 0 && (
                <section aria-labelledby="secao-participando">
                  <div className="mb-5 sm:mb-6">
                    <h2 id="secao-participando" className="text-lg sm:text-xl font-semibold text-gray-800" tabIndex={0}>
                      Iniciativas que estou participando:
                    </h2>
                  </div>
                  <div className="space-y-3 sm:space-y-4">
                    {participando.map((entrega, index) => {
                      const globalIndex = minhasIniciativas.length + index;
                      return (
                        <CardComponent
                          key={entrega._id || globalIndex}
                          entrega={entrega}
                          index={globalIndex}
                          isExpanded={expandedCards.has(globalIndex)}
                          onToggle={() => toggleCard(globalIndex)}
                          onUpdate={carregarEntregas}
                          currentUserId={user?._id}
                        />
                      );
                    })}
                  </div>
                </section>
              )}
            </>
          )}
        </main>
      </div>
    </div>
  );
}
